import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { credentialsService } from '../services/credentialsService';
import { CredentialType, CREDENTIAL_TYPE_LABELS } from '../types/entity';

export const credentialAddCommand: SelfContainedCommand = {
  id: 'credential_add',
  description: '🔐 ADD: Store a new credential (login, API key, payment card, etc.) in the Password Vault',
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Human label for this credential (e.g. "Gmail", "GitHub Token")',
        required: true,
      },
      {
        name: 'credentialType',
        type: 'string',
        description: 'Type: login | payment_card | identity | secure_note | api_key | oauth_app | token | custom',
        required: true,
      },
      {
        name: 'fields',
        type: 'object',
        description: 'Secret fields dict. For login: {password}. For api_key: {key}. For oauth_app: {clientId, clientSecret}. For token: {token}.',
        required: true,
      },
      {
        name: 'service',
        type: 'string',
        description: 'Service slug (github, stripe, google, …)',
        required: false,
      },
      {
        name: 'username',
        type: 'string',
        description: 'Username or email (for logins — stored unencrypted for display)',
        required: false,
      },
      {
        name: 'url',
        type: 'string',
        description: 'Website URL (for logins)',
        required: false,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { name: 'GitHub Token', credentialType: 'token', fields: { token: 'ghp_abc123' }, service: 'github' },
        expectedOutput: {
          status: 'success',
          message: 'Credential stored: GitHub Token',
          format: 'json',
          data: { credentialId: 'cred_123' },
        },
        description: 'Store a GitHub personal access token',
      },
    ],
    bad: [],
  },

  handler: async (
    params: {
      name: string;
      credentialType: CredentialType;
      fields: Record<string, string>;
      service?: string;
      username?: string;
      url?: string;
    },
    _context: CommandExecutionContext
  ): Promise<StandardToolResult> => {
    try {
      const credential = await credentialsService.create({
        name: params.name,
        credentialType: params.credentialType,
        fields: params.fields,
        service: params.service ?? '',
        username: params.username ?? '',
        url: params.url ?? '',
      });

      return createSuccessResult(`Credential stored: ${params.name}`, {
        credentialId: credential._id,
        name: credential.name,
        credentialType: credential.skeleton.credentialType,
      });
    } catch (error) {
      return createErrorResult(
        'Failed to store credential',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  },
};

export const credentialListCommand: SelfContainedCommand = {
  id: 'credential_list',
  description: '🔐 LIST: List stored credentials by name and type — never returns secret field values',
  autonomyLevel: 'auto',
  visualTreatment: 'silent',

  schema: {
    parameters: [
      {
        name: 'credentialType',
        type: 'string',
        description: 'Optional filter by type: login | api_key | payment_card | etc.',
        required: false,
      },
      {
        name: 'service',
        type: 'string',
        description: 'Optional filter by service slug',
        required: false,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { credentialType: 'api_key' },
        expectedOutput: {
          status: 'success',
          message: '3 credentials found',
          format: 'json',
          data: { credentials: [{ name: 'GitHub Token', service: 'github' }] },
        },
        description: 'List all API keys',
      },
    ],
    bad: [],
  },

  handler: async (
    params: { credentialType?: CredentialType; service?: string },
    _context: CommandExecutionContext
  ): Promise<StandardToolResult> => {
    try {
      const credentials = await credentialsService.list({
        credentialType: params.credentialType,
        service: params.service,
      });

      // Return only non-sensitive metadata — never fields
      const summary = credentials.map((c) => ({
        id: c._id,
        name: c.name,
        credentialType: c.skeleton.credentialType,
        typeLabel: CREDENTIAL_TYPE_LABELS[c.skeleton.credentialType],
        service: c.skeleton.service,
        username: c.skeleton.username,
        url: c.skeleton.url,
        tags: c.skeleton.tags,
      }));

      return createSuccessResult(
        `${credentials.length} credential${credentials.length !== 1 ? 's' : ''} found`,
        { credentials: summary }
      );
    } catch (error) {
      return createErrorResult(
        'Failed to list credentials',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  },
};

export const passwordVaultCommands = [credentialAddCommand, credentialListCommand];
